import { useEffect, useState } from "react";
import axios from "axios";
import { useQuery } from "react-query";

import { INIT_FILTER_VALUES } from "../../constants";

/**
 * Custom hook responsible for controlling the state related to the
 * Map filter controls
 * Accepts one argument, `onFilterChange` that is a callback that
 * can be run whenever a filter value changes
 */
const useFilters = ({ onFilterChange, isMapLoaded }) => {
  const [filterValues, setFilterValues] = useState(INIT_FILTER_VALUES);
  const [defaultFilterValues, setDefaultFilterValues] = useState(null);

  /**
   * Hit the API and get a list of options for each filter
   * The endpoint returns an object where each key/value pair
   * represents the options for a specific filter
   * This approach allows us to make a single API request instead
   * of multiple
   */
  const { data } = useQuery(
    ["filterData"],
    async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_ENDPOINT}/api/public-map/filters`
        );
        return response;
      } catch (err) {
        console.error(err);
      }
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  /**
   * Set the options and initial value for each filter based on
   * what is returned from the DB
   */
  useEffect(() => {
    if (isMapLoaded && data?.data) {
      const newFilterValues = {
        ...filterValues,
        locationTypes: {
          ...filterValues.locationTypes,
          options: data?.data?.locationTypes || [],
          value: data?.data?.locationTypes.map(({ value }) => value) || [],
        },
        organizations: {
          ...filterValues.organizations,
          options: data?.data?.organizations || [],
          value: data?.data?.organizations.map(({ value }) => value) || [],
        },
        basins: {
          ...filterValues.basins,
          options: data?.data?.basins || [],
          value: data?.data?.basins.map(({ value }) => value) || [],
        },
        sources: {
          ...filterValues.sources,
          options: data?.data?.sources || [],
          value: data?.data?.sources.map(({ value }) => value) || [],
        },
        locations: {
          ...filterValues.locations,
          options: data?.data?.locations || [],
          value: data?.data?.locations.map(({ value }) => value) || [],
        },
        graphMode: {
          ...filterValues.graphMode,
          value: data?.data?.graphMode || [""],
        },
        graphModeReference: {
          ...filterValues.graphMode,
          value: data?.data?.graphMode || [""],
        },
      };
      onFilterChange(newFilterValues);
      setFilterValues(newFilterValues);
      setDefaultFilterValues(newFilterValues);
    }
  }, [data?.data, isMapLoaded]); //eslint-disable-line

  const handleResetAll = () => {
    onFilterChange(defaultFilterValues);
    setFilterValues(defaultFilterValues);
  };

  const handleSelectAll = (name) => {
    setFilterValues((prevState) => {
      const newState = {
        ...prevState,
        [name]: {
          ...prevState[name],
          value: data?.data?.[name]?.map(({ value }) => value),
        },
      };
      onFilterChange(newState);
      return newState;
    });
  };

  const handleSelectNone = (name) => {
    setFilterValues((prevState) => {
      const newState = {
        ...prevState,
        [name]: {
          ...prevState[name],
          value: [],
        },
      };
      onFilterChange(newState);
      return newState;
    });
  };

  const handleFilterValues = (event) => {
    const { checked, name, value, batch } = event.target;

    const type = filterValues[name]?.type || "locations-autocomplete";

    if (
      type === "multi-select" ||
      type === "multi-select-array" ||
      type === "locations-autocomplete"
    ) {
      // Handle batch updates (for select all/none)
      if (batch && Array.isArray(value)) {
        setFilterValues((prevState) => {
          let newValue = [...(prevState[name]?.value || [])];

          if (checked) {
            // Add all values that aren't already present
            value.forEach((val) => {
              if (!newValue.includes(val)) {
                newValue.push(val);
              }
            });
          } else {
            // Remove all values
            newValue = newValue.filter((val) => !value.includes(val));
          }

          const newState = {
            ...prevState,
            [name]: {
              ...prevState[name],
              value: newValue,
            },
          };
          onFilterChange(newState);
          return newState;
        });
      } else {
        // Handle single value updates
        setFilterValues((prevState) => {
          const newValue = [...(prevState[name]?.value || [])];
          const existingIndex = newValue.indexOf(value);
          if (existingIndex > -1) {
            newValue.splice(existingIndex, 1);
          } else {
            newValue.push(value);
          }
          const newState = {
            ...prevState,
            [name]: {
              ...prevState[name],
              value: newValue,
            },
          };
          onFilterChange(newState);
          return newState;
        });
      }
    } else if (type === "boolean") {
      setFilterValues((prevState) => {
        const newState = {
          ...prevState,
          [name]: {
            ...prevState[name],
            value: checked,
          },
        };
        onFilterChange(newState);
        return newState;
      });
    } else if (type === "select") {
      setFilterValues((prevState) => {
        const newState = {
          ...prevState,
          [name]: {
            ...prevState[name],
            value: value,
          },
        };
        // onFilterChange(newState);
        return newState;
      });
    }
  };

  return {
    filterValues,
    setFilterValues,
    handleFilterValues,
    handleSelectAll,
    handleSelectNone,
    handleResetAll,
  };
};

export default useFilters;
